import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { QueryParams, SortOrder, TabType } from './types';

const DEFAULT_PER_PAGE = 25;
const DEFAULT_PAGE = 1;
const DEFAULT_TAB: TabType = 'unbilled';
const DEFAULT_DATE_RANGE_DAYS = 30;

/** Format a Date as YYYY-MM-DD in local time */
const formatLocalDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Get a default date range of the last N days (today inclusive, end date exclusive) */
const getDefaultDateRange = (days: number = DEFAULT_DATE_RANGE_DAYS) => {
  const from = new Date();
  from.setDate(from.getDate() - days);
  const to = new Date();
  to.setDate(to.getDate() + 1); // +1 because backend treats end date as exclusive
  return { from: formatLocalDate(from), to: formatLocalDate(to) };
};

// Default sort fields for each tab that match backend defaults
const DEFAULT_SORT_BY_TAB: Record<TabType, string> = {
  unbilled: 'scheduled_for',
  claims: 'dateOfService',
  remittances: 'payment_date',
  statements: 'created_at',
  history: 'completed_at',
};
const DEFAULT_SORT_ORDER: SortOrder = 'desc';

const DEFAULT_STATUS_BY_TAB: Record<TabType, string | undefined> = {
  unbilled: 'completed',
  claims: undefined,
  remittances: undefined,
  statements: undefined,
  history: undefined,
};

const SESSION_STORAGE_KEY = 'billing-filters';

const persistToSession = (params: URLSearchParams) => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, params.toString());
  } catch {
    // Ignore storage errors
  }
};

export const useQueryParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialized = useRef(false);

  // On first mount, if no URL params exist, restore from sessionStorage or apply defaults
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    // If the URL already has params, persist them and bail
    if (searchParams.toString()) {
      persistToSession(searchParams);
      return;
    }

    // Try restoring from sessionStorage
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (stored) {
        setSearchParams(new URLSearchParams(stored), { replace: true });
        return;
      }
    } catch {
      // Ignore storage errors
    }

    // No stored params — apply defaults (including last 30 days)
    const defaults = new URLSearchParams();
    defaults.set('status', 'completed');
    defaults.set('sort', DEFAULT_SORT_BY_TAB[DEFAULT_TAB]);
    defaults.set('order', DEFAULT_SORT_ORDER);
    defaults.set('page', String(DEFAULT_PAGE));
    defaults.set('perPage', String(DEFAULT_PER_PAGE));
    const defaultRange = getDefaultDateRange();
    defaults.set('from', defaultRange.from);
    defaults.set('to', defaultRange.to);
    setSearchParams(defaults, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const params = useMemo<QueryParams>(() => {
    const tab = (searchParams.get('tab') as TabType) || DEFAULT_TAB;
    return {
      tab,
      patient: searchParams.get('patient') || undefined,
      from: searchParams.get('from') || undefined,
      to: searchParams.get('to') || undefined,
      status: searchParams.get('status') || undefined,
      providers: searchParams.get('providers') || undefined,
      hasClaim: searchParams.get('hasClaim') || undefined,
      readyToBill: searchParams.get('readyToBill') || undefined,
      sort: searchParams.get('sort') || DEFAULT_SORT_BY_TAB[tab],
      order: (searchParams.get('order') as SortOrder) || DEFAULT_SORT_ORDER,
      page: Number(searchParams.get('page')) || DEFAULT_PAGE,
      perPage: Number(searchParams.get('perPage')) || DEFAULT_PER_PAGE,
      // Remittances-specific
      payerName: searchParams.get('payerName') || undefined,
      paymentMethod: searchParams.get('paymentMethod') || undefined,
      queue: searchParams.get('queue') || undefined,
    };
  }, [searchParams]);

  const updateParams = useCallback(
    (updates: Partial<QueryParams>, resetPage = false) => {
      const newParams = new URLSearchParams(searchParams);

      // Apply updates
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });

      // Reset page to 1 when filters or sorting changes (but not when page itself changes)
      if (resetPage && !('page' in updates)) {
        newParams.set('page', '1');
      }

      setSearchParams(newParams);
      persistToSession(newParams);
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    const newParams = new URLSearchParams();
    newParams.set('tab', params.tab);
    newParams.set('page', '1');
    newParams.set('perPage', String(params.perPage));
    // Preserve default sort settings
    newParams.set('sort', DEFAULT_SORT_BY_TAB[params.tab]);
    newParams.set('order', DEFAULT_SORT_ORDER);
    // Apply default status for the current tab
    const defaultStatus = DEFAULT_STATUS_BY_TAB[params.tab];
    if (defaultStatus) {
      newParams.set('status', defaultStatus);
    }
    // Restore default 30-day date range
    const defaultRange = getDefaultDateRange();
    newParams.set('from', defaultRange.from);
    newParams.set('to', defaultRange.to);
    setSearchParams(newParams);
    persistToSession(newParams);
  }, [params.tab, params.perPage, setSearchParams]);

  const hasActiveFilters = useMemo(() => {
    const tab = params.tab;
    const defaultStatus = DEFAULT_STATUS_BY_TAB[tab];
    const hasNonDefaultStatus = params.status && params.status !== defaultStatus;
    // Check if the date range differs from the default 30-day range
    const defaultRange = getDefaultDateRange();
    const hasNonDefaultDateRange =
      (params.from && params.from !== defaultRange.from) ||
      (params.to && params.to !== defaultRange.to);
    return Boolean(
      params.patient ||
      hasNonDefaultDateRange ||
      hasNonDefaultStatus ||
      params.providers ||
      params.hasClaim ||
      params.readyToBill ||
      params.payerName ||
      params.paymentMethod ||
      params.queue,
    );
  }, [params]);

  return {
    params,
    updateParams,
    clearFilters,
    hasActiveFilters,
  };
};
